const money = (amount, currency = 'GBP') => new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(amount / 100);

export function getOfferState(config, selections, requestedType) {
  const products = selections.map((id) => config.products.find((product) => String(product.id) === String(id)));
  const valid = products.length === 2 && products.every((product) => product && product.available);
  const oneTimeTotal = valid ? products.reduce((total, product) => total + product.price, 0) : 0;
  const renewalTotal = valid ? products.reduce((total, product) => total + (product.planPrice || 0), 0) : 0;
  const comparisonRrp = Number.isInteger(config.rrpPrice) && config.rrpPrice > 0 ? config.rrpPrice : oneTimeTotal;
  const subscriptionAvailable = Boolean(valid && config.offerVerified && config.currency === 'GBP' && config.discountCode && products.every((product) => product.planId && product.planPrice > 0) && products[0].planId === products[1].planId && renewalTotal === config.expectedRenewal);
  const purchaseType = requestedType === 'subscription' && subscriptionAvailable ? 'subscription' : 'one_time';
  return { valid, products, oneTimeTotal, renewalTotal, comparisonRrp, subscriptionAvailable, purchaseType, today: purchaseType === 'subscription' ? config.introPrice : oneTimeTotal };
}

export function buildCartItems(config, selections, purchaseType) {
  const state = getOfferState(config, selections, purchaseType);
  if (!state.valid) throw new Error('Please choose two available boxes.');
  if (purchaseType === 'subscription' && !state.subscriptionAvailable) throw new Error('The starter subscription is not available. Please choose a one-time purchase.');
  const lines = new Map();
  for (const product of state.products) {
    const planId = purchaseType === 'subscription' ? product.planId : null;
    const key = String(product.id) + ':' + String(planId || '');
    if (lines.has(key)) { lines.get(key).quantity += 1; continue; }
    const item = { id: product.id, quantity: 1, properties: { _ampm_campaign: config.campaignId, _ampm_offer: config.offerId } };
    if (planId) item.selling_plan = planId;
    lines.set(key, item);
  }
  return [...lines.values()];
}

export function mountDuo(section) {
  if (section.dataset.duoReady === 'true') return;
  const configElement = section.querySelector('[data-duo-config]');
  const form = section.querySelector('[data-duo-form]');
  if (!configElement || !form) return;
  const config = JSON.parse(configElement.textContent);
  section.dataset.duoReady = 'true';
  if (config.suppressPopups) document.body.classList.add('ampm-paid-route--no-promos');
  const selects = [...form.querySelectorAll('[data-duo-flavour]')];
  const subscription = form.querySelector('input[value="subscription"]');
  const oneTime = form.querySelector('input[value="one_time"]');
  const submit = form.querySelector('[data-duo-submit]');
  const error = form.querySelector('[data-duo-error]');
  const status = form.querySelector('[data-duo-status]');
  const sticky = section.querySelector('[data-duo-sticky]');
  const chooseButtons = [...section.querySelectorAll('[data-duo-choose]')];
  let busy = false;
  const selections = () => selects.map((select) => select.value);
  const format = (amount) => money(amount, config.currency);
  const text = (selector, value) => section.querySelectorAll(selector).forEach((element) => { if (element.textContent !== value) element.textContent = value; });
  const emit = (name, detail = {}) => {
    const payload = { campaign_id: config.campaignId, offer_id: config.offerId, ...detail };
    window.dispatchEvent(new CustomEvent(name, { detail: payload }));
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: name, ...payload });
  };
  const update = () => {
    const state = getOfferState(config, selections(), subscription.checked ? 'subscription' : 'one_time');
    subscription.disabled = busy || !state.subscriptionAvailable;
    if (!state.subscriptionAvailable) { subscription.checked = false; oneTime.checked = true; }
    oneTime.disabled = busy || !state.valid;
    submit.disabled = busy || !state.valid;
    selects.forEach((select) => { select.disabled = busy; });
    const isSubscription = state.purchaseType === 'subscription';
    section.dataset.purchaseType = state.purchaseType;
    text('[data-duo-today]', state.valid ? format(state.today) : 'Unavailable');
    text('[data-duo-one-time]', format(state.oneTimeTotal));
    text('[data-duo-compare]', format(state.comparisonRrp));
    text('[data-duo-saving]', format(state.comparisonRrp - config.introPrice));
    text('[data-duo-unit]', state.valid ? format(state.today / 60) + ' per sachet' : 'Choose available flavours');
    text('[data-duo-price-label]', isSubscription ? 'First subscription delivery' : 'One-time purchase');
    text('[data-duo-renewal]', 'Then 2 boxes for ' + format(state.renewalTotal) + ' every 8 weeks.');
    text('[data-duo-badge]', isSubscription ? 'Save ' + format(state.comparisonRrp - config.introPrice) + ' against RRP on your first delivery' : '2 boxes × 30 sachets');
    text('[data-duo-sticky-mode]', isSubscription ? 'First subscription delivery' : 'Two-box one-time purchase');
    text('[data-duo-sticky-renewal]', 'Then ' + format(state.renewalTotal) + ' / 8 weeks');
    text('[data-duo-terms]', isSubscription ? 'First delivery ' + format(config.introPrice) + '. Then 2 boxes for ' + format(state.renewalTotal) + ' every 8 weeks. Free UK delivery. Skip, pause or cancel before renewal. Introductory offer limited to one use per customer.' : 'Two boxes in one delivery. No subscription or automatic renewal. Delivery is calculated at checkout.');
    section.querySelectorAll('[data-duo-subscription-only]').forEach((element) => { element.hidden = !isSubscription; });
    section.querySelectorAll('[data-duo-one-time-only]').forEach((element) => { element.hidden = isSubscription; });
    section.querySelectorAll('[data-duo-unavailable]').forEach((element) => { element.hidden = state.subscriptionAvailable; });
    section.querySelectorAll('[data-duo-offer-ready]').forEach((element) => { element.hidden = !state.subscriptionAvailable; });
    submit.textContent = busy ? 'Adding your boxes…' : !state.valid ? 'Currently unavailable' : (isSubscription ? 'Start my subscription — ' : 'Buy two boxes — ') + format(state.today);
    chooseButtons.forEach((button) => { button.disabled = busy || !state.valid; });
    state.products.forEach((product, index) => {
      if (!product) return;
      const image = section.querySelector('[data-duo-image="' + index + '"]');
      if (image && product.image) { image.src = product.image; image.removeAttribute('srcset'); image.alt = product.title + ', box of 30 sachets'; }
      text('[data-duo-image-label="' + index + '"]', product.flavour + ' · 30 sachets');
    });
    return state;
  };
  selects.forEach((select, index) => {
    if (select.selectedOptions[0]?.disabled) {
      const available = [...select.options].find((option) => !option.disabled);
      if (available) select.value = available.value;
    }
    select.addEventListener('change', () => { update(); emit('ampm_flavour_selection', { box: index + 1, product: select.selectedOptions[0]?.textContent }); });
  });
  form.querySelectorAll('input[name="purchase_type"]').forEach((radio) => radio.addEventListener('change', () => { const state = update(); emit('ampm_offer_selection', { purchase_type: state.purchaseType }); }));
  chooseButtons.forEach((button) => button.addEventListener('click', () => {
    form.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    selects[0].focus({ preventScroll: true });
  }));
  const updateSticky = () => {
    const rect = submit.getBoundingClientRect();
    const visible = window.matchMedia('(max-width: 899px)').matches && !(rect.top >= 0 && rect.bottom <= window.innerHeight);
    sticky.hidden = !visible;
    sticky.setAttribute('aria-hidden', String(!visible));
    sticky.querySelector('button').tabIndex = visible ? 0 : -1;
  };
  if ('IntersectionObserver' in window) new IntersectionObserver(updateSticky, { threshold: [0, 1] }).observe(submit);
  window.addEventListener('resize', updateSticky, { passive: true });
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    busy = false;
    form.removeAttribute('aria-busy');
    status.textContent = '';
    update();
    updateSticky();
  });
  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (busy) return;
    error.hidden = true;
    const state = update();
    let items;
    try { items = buildCartItems(config, selections(), state.purchaseType); }
    catch (caught) { error.textContent = caught.message; error.hidden = false; error.focus(); return; }
    busy = true;
    form.setAttribute('aria-busy', 'true');
    status.textContent = 'Adding your two boxes to the bag.';
    update();
    try {
      const response = await fetch(config.cartAddUrl, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items }) });
      if (!response.ok) throw new Error('Your boxes could not be added. Please check the flavours and try again.');
      emit('ampm_add_to_cart', { purchase_type: state.purchaseType, bundle: true, item_count: 2 });
      window.location.assign(state.purchaseType === 'subscription' ? config.rootUrl + 'discount/' + encodeURIComponent(config.discountCode) + '?redirect=' + encodeURIComponent(config.cartUrl) : config.cartUrl);
    } catch (caught) {
      busy = false;
      form.removeAttribute('aria-busy');
      status.textContent = '';
      update();
      error.textContent = caught.message || 'Something went wrong. Please try again.';
      error.hidden = false;
      error.focus();
    }
  });
  update();
  updateSticky();
}

if (typeof document !== 'undefined') {
  const initialise = (root = document) => root.querySelectorAll('[data-electrolyte-duo]').forEach(mountDuo);
  initialise();
  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
}
